import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import NotFound from '../NotFound.jsx';
import '../../styles/InvitationLoader.css';
import logicaLogo from '../../assets/images/Logica (Black and White).webp';

const designPageLoaders = import.meta.glob('./designs/*.jsx');
const PETRINE_INVITATION_ID = 'petrine-mickayla-petring-birthday';

function normalizeInvitationId(value) {
  return String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}

const loadersByInvitationId = Object.fromEntries(
  Object.entries(designPageLoaders).map(([filePath, loader]) => {
    const match = filePath.match(/\/([^/]+)\.jsx$/);
    const derivedInvitationId = normalizeInvitationId(match?.[1] || '');
    return [derivedInvitationId, loader];
  })
);

function PetrineHeroTextPatch({ invitationId, children }) {
  useEffect(() => {
    if (invitationId !== PETRINE_INVITATION_ID) return undefined;

    let frame = 0;
    const originalFetch = window.fetch;

    const injectEmailField = () => {
      const form = document.querySelector('.pb-page form.pb-form');
      if (!form || form.querySelector('#pb-email')) return;

      const nameField = form.querySelector('#pb-name')?.closest('.pb-field');
      const field = document.createElement('div');
      field.className = 'pb-field pb-email-field';
      field.innerHTML = `
        <label for="pb-email">Your Email Address *</label>
        <input
          type="email"
          id="pb-email"
          required
          autocomplete="email"
          placeholder="Enter your email for the event reminder"
          style="width:100%;padding:11px 14px;border:2px solid rgba(236,72,153,.28);border-radius:10px;font-family:'Nunito',sans-serif;font-size:15px;font-weight:600;color:#1C1917;background:#fff;outline:none;transition:border-color .2s,box-shadow .2s;"
        />
      `;

      const emailInput = field.querySelector('#pb-email');
      emailInput?.addEventListener('focus', () => {
        emailInput.style.borderColor = '#EC4899';
        emailInput.style.boxShadow = '0 0 0 3px rgba(236,72,153,.14)';
      });
      emailInput?.addEventListener('blur', () => {
        emailInput.style.borderColor = 'rgba(236,72,153,.28)';
        emailInput.style.boxShadow = 'none';
      });

      if (nameField?.parentNode) {
        nameField.parentNode.insertBefore(field, nameField.nextSibling);
      } else {
        form.insertBefore(field, form.firstChild);
      }
    };

    const updateHeroTextAndForm = () => {
      const subtitle = document.querySelector('.pb-page .pb-hero-pre');
      if (subtitle && subtitle.textContent !== "We'd like to celebrate") {
        subtitle.textContent = "We'd like to celebrate";
      }

      const heroName = document.querySelector('.pb-page .pb-hero-one');
      if (heroName && heroName.textContent !== "Petrine Mickayla's") {
        heroName.textContent = "Petrine Mickayla's";
        heroName.setAttribute('aria-label', "Petrine Mickayla's");
      }

      injectEmailField();
    };

    const scheduleUpdate = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(updateHeroTextAndForm);
    };

    window.fetch = (input, init = {}) => {
      try {
        const url = typeof input === 'string' ? input : input?.url || '';
        const method = String(init?.method || 'GET').toUpperCase();
        const isRsvpPost = method === 'POST' && /\/rsvp\/?$/.test(url);

        if (isRsvpPost && init?.body) {
          const payload = JSON.parse(init.body);
          if (payload?.invitationId === PETRINE_INVITATION_ID) {
            const emailInput = document.querySelector('#pb-email');
            payload.email = String(emailInput?.value || payload.email || '').trim().toLowerCase();
            init = { ...init, body: JSON.stringify(payload) };
          }
        }
      } catch {
        // Let the original request proceed unchanged if parsing fails.
      }

      return originalFetch(input, init);
    };

    scheduleUpdate();

    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.fetch = originalFetch;
    };
  }, [invitationId]);

  return children;
}

export default function CustomDesignInvitation() {
  const { invitationId } = useParams();
  const normalizedInvitationId = useMemo(() => normalizeInvitationId(invitationId), [invitationId]);
  const [DesignComponent, setDesignComponent] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let isActive = true;
    const loader = loadersByInvitationId[normalizedInvitationId];

    setDesignComponent(null);

    if (!loader) {
      setStatus('not-found');
      return () => {
        isActive = false;
      };
    }

    setStatus('loading');

    loader()
      .then((module) => {
        if (!isActive || typeof module.default !== 'function') return;
        setDesignComponent(() => module.default);
        setStatus('ready');
      })
      .catch(() => {
        if (isActive) setStatus('not-found');
      });

    return () => {
      isActive = false;
    };
  }, [normalizedInvitationId]);

  if (status === 'not-found') {
    return <NotFound />;
  }

  if (status !== 'ready' || !DesignComponent) {
    return (
      <main className="inv-loader-page" aria-live="polite" aria-label="Loading invitation">
        {/* Floating fireflies */}
        <div className="inv-loader-particles" aria-hidden="true">
          {Array.from({ length: 15 }).map((_, i) => (
            <div key={i} className="inv-loader-particle" />
          ))}
        </div>

        {/* Center content */}
        <div className="inv-loader-center">
          {/* Ornament */}
          <div className="inv-loader-ornament" aria-hidden="true">
            <div className="inv-loader-ornament-line" />
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2 L14.5 9.5 L22 12 L14.5 14.5 L12 22 L9.5 14.5 L2 12 L9.5 9.5 Z"
                stroke="currentColor" strokeWidth="1" fill="currentColor" fillOpacity="0.25" />
            </svg>
            <div className="inv-loader-ornament-line" />
          </div>

          {/* Script headline */}
          <p className="inv-loader-script">Your Invitation</p>

          {/* Subtitle */}
          <p className="inv-loader-subtitle">Awaits &nbsp;✦&nbsp; Preparing your experience</p>

          {/* Sweeping progress bar */}
          <div className="inv-loader-bar-wrap" role="progressbar" aria-label="Loading">
            <div className="inv-loader-bar" />
          </div>

          {/* Dot pulse */}
          <div className="inv-loader-dots" aria-hidden="true">
            <div className="inv-loader-dot" />
            <div className="inv-loader-dot" />
            <div className="inv-loader-dot" />
          </div>
        </div>

        {/* Brand */}
        <a
          href="https://logicatechnology.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inv-loader-brand"
          aria-label="Logica Technology"
        >
          <img src={logicaLogo} alt="" aria-hidden="true" />
          <span>Logica Technology</span>
        </a>
      </main>
    );
  }

  return (
    <PetrineHeroTextPatch invitationId={normalizedInvitationId}>
      <DesignComponent />
    </PetrineHeroTextPatch>
  );
}
